// Inteligencia › Entradas y salidas: qué vehículos están en la obra a cada hora, cuándo entró y salió cada uno,
// línea de tiempo por vehículo (tipo Gantt) y reporte PDF del día o del período.
// El seguimiento es el de la página Vehículos (vehiculosData.js) sobre las detecciones del modelo nuevo (iaV3):
// mismo vehículo = mismo tipo + color + lugar donde estaciona. No hay lectura de patentes (a la distancia de la
// cámara quedan de 20-40 px): por eso se habla de "vehículo probable".
import React, { useEffect, useMemo, useState } from 'react';
import { T, Btn, Card, Chip } from '../ui';
import { fmtHM, fmtKey, fmtDow, dayStartMs, fmtDM } from '../iaData';
import { Photo, Visor } from '../CalendarioObra';
import { precargar, anchoObjetivo } from '../fotos';
import { buildTracks, buildVehicles, computeColors, typeInfo, colorInfo, vehicleName, VEH } from './vehiculosData';
import { porDia, recorte, plural } from './datosV3';
import { useFotosV3, useNarrow, Encabezado, Rango, Nota, Cargando, Vacio, SinAnalisis, Kpi, textoRango } from './comun';
import { nuevoPdf, imagenRecorte, nombreArchivo } from './pdf';

const H0 = VEH.hourFrom, H1 = VEH.hourTo;
const R169 = 16 / 9;
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const dur = (min) => (min < 60 ? `${Math.round(min)} min` : `${Math.floor(min / 60)} h ${String(Math.round(min % 60)).padStart(2, '0')}`);

// Movimientos de un vehículo en un día: cada tramo = una entrada y una salida, con el margen de la foto anterior/siguiente
function movimientos(dv, fotosDia) {
  return dv.tracks.map((t) => {
    const prev = [...fotosDia].reverse().find((p) => p.ms < t.first);
    const next = fotosDia.find((p) => p.ms > t.last);
    return {
      t,
      entra: t.first, entraDesde: prev ? prev.ms : null, // entró entre la foto anterior y esta
      sale: t.last, saleHasta: next ? next.ms : null, // salió entre esta y la siguiente
      min: Math.max(0, (t.last - t.first) / 60e3) + (next ? (next.ms - t.last) / 120e3 : 0),
    };
  });
}

const textoEntrada = (m) => (m.entraDesde == null ? `Ya estaba a las ${fmtHM(m.entra)} (primera foto)` : `Entró entre ${fmtHM(m.entraDesde)} y ${fmtHM(m.entra)}`);
const textoSalida = (m) => (m.saleHasta == null ? `Seguía a las ${fmtHM(m.sale)} (última foto)` : `Salió entre ${fmtHM(m.sale)} y ${fmtHM(m.saleHasta)}`);

const Muestra = ({ color }) => {
  const c = colorInfo(color);
  return <span title={c ? c.label : 'color sin determinar'} style={{ width: 11, height: 11, borderRadius: 999, flex: 'none', display: 'inline-block', background: c ? c.hex : 'repeating-linear-gradient(45deg,#e5e7eb 0 3px,#fff 3px 6px)', border: '1px solid rgba(0,0,0,.2)' }} />;
};

const ICONO = { auto: '🚗', camioneta: '🚙', camion: '🚚', volquete: '🚛', mixer: '🌀', bomba: '🏗️', excavadora: '⛏️', retro: '🚜', minicargadora: '🚜', grua: '🏗️', autoelevador: '🏭' };
// Mejor vista del vehículo en el día: la caja más grande y más segura
const tam = (d) => d.conf ** 2 * Math.sqrt((d.x2 - d.x1) * (d.y2 - d.y1));
const mejorObs = (obs) => obs.reduce((a, o) => (tam(o.d) > tam(a.d) ? o : a));
const recorteVeh = (o) => recorte(o.p, o.d, { k: 1.5, ratio: R169, min: 0.05 });
// Foto que se muestra al entrar a un día: la que tiene más vehículos
function fotoPrincipal(fotosDia, tracks) {
  let mejor = 0, n = -1;
  fotosDia.forEach((p, i) => { const k = tracks.filter((t) => t.obs.some((o) => o.p.id === p.id)).length; if (k > n) { n = k; mejor = i; } });
  return mejor;
}
// Lista para el Visor: todas las fotos de ese vehículo (en el orden del día) con su caja
const fotosDeVehiculo = (obs, color) => [...obs].sort((a, b) => a.p.ms - b.p.ms).map((o) => ({
  photo: o.p, label: `${fmtDM(o.p.ms)} ${fmtHM(o.p.ms)}`, boxes: [{ ...o.d, color }], zoom: recorte(o.p, o.d, { k: 3, ratio: R169, min: 0.15 }),
}));

// Línea de tiempo del día (6 a 20 h): una fila por vehículo, que empieza con el RECORTE del vehículo (su mejor foto del día)
// y el tipo en un chip. Tocar el recorte abre todas sus fotos del día; pasar el mouse (o tocar) un tramo muestra la foto
// de ese momento. En el celular el recorte queda fijo a la izquierda y la barra se desplaza.
function Gantt({ filas, dia, hora, onHora, onElegir, sel, narrow, onVerFotos }) {
  const [tip, setTip] = useState(null); // { vid, i, o, left }
  const base = dayStartMs(dia);
  const x = (ms) => Math.max(0, Math.min(100, (100 * ((ms - base) / 3600e3 - H0)) / (H1 - H0)));
  const horas = Array.from({ length: H1 - H0 + 1 }, (_, i) => H0 + i);
  const fotoW = narrow ? 96 : 112;
  const nombreW = fotoW + 12;
  return (
    <div style={{ overflowX: narrow ? 'auto' : 'visible' }} onMouseLeave={() => setTip(null)}>
      <div style={{ minWidth: narrow ? 560 : 0 }}>
        <div style={{ display: 'flex', fontSize: 10.5, color: T.muted, marginLeft: nombreW, position: 'relative', height: 16 }}>
          {horas.map((h) => (h % (narrow ? 2 : 1) === 0 ? <span key={h} style={{ position: 'absolute', left: `${(100 * (h - H0)) / (H1 - H0)}%`, transform: `translateX(${h === H0 ? 0 : h === H1 ? -100 : -50}%)` }}>{h}</span> : null))}
        </div>
        <div style={{ position: 'relative' }}>
          {filas.map(({ v, dv, movs }, fila) => {
            const ti = typeInfo(v.type);
            const on = sel === v.id;
            const obsDia = dv.tracks.flatMap((t) => t.obs);
            const mej = mejorObs(obsDia);
            return (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center', minHeight: (fotoW * 9) / 16 + 8, background: on ? '#eff6ff' : 'transparent', borderRadius: 8, position: 'relative' }}>
                <div style={{ width: nombreW, flex: 'none', padding: '4px 10px 4px 2px', position: narrow ? 'sticky' : 'static', left: 0, zIndex: 2, background: on ? '#eff6ff' : '#fff' }}>
                  <Photo photo={mej.p} crop={recorteVeh(mej)} onClick={() => onVerFotos(fotosDeVehiculo(obsDia, ti.color))}
                    style={{ width: fotoW, cursor: 'zoom-in', borderRadius: 6, outline: on ? `2px solid ${T.blue}` : 'none' }}>
                    <span title={`${vehicleName(v)} · vehículo probable`} style={{ position: 'absolute', left: 3, bottom: 3, fontSize: 10.5, fontWeight: 700, background: 'rgba(255,255,255,.92)', color: ti.color, border: `1px solid ${ti.color}66`, borderRadius: 999, padding: '0 6px', lineHeight: '16px', whiteSpace: 'nowrap', maxWidth: 'calc(100% - 8px)', overflow: 'hidden', textOverflow: 'ellipsis', boxSizing: 'border-box' }}>
                      {ICONO[v.type] || '🚗'} {ti.label}
                    </span>
                  </Photo>
                </div>
                <div style={{ position: 'relative', flex: 1, height: 16, background: '#f3f4f6', borderRadius: 4, cursor: 'pointer' }} onClick={() => onElegir(v.id)}>
                  {movs.map((m, i) => {
                    const a = m.entraDesde != null ? (m.entraDesde + m.entra) / 2 : m.entra;
                    const b = m.saleHasta != null ? (m.sale + m.saleHasta) / 2 : m.sale;
                    const enTramo = (ev) => {
                      // foto del vehículo más cercana al lugar de la barra donde está el mouse o el dedo
                      const r = ev.currentTarget.parentNode.getBoundingClientRect();
                      const ms = base + (H0 + ((ev.clientX - r.left) / r.width) * (H1 - H0)) * 3600e3;
                      const o = m.t.obs.reduce((p, q) => (Math.abs(q.p.ms - ms) < Math.abs(p.p.ms - ms) ? q : p));
                      if (!tip || tip.o !== o) setTip({ vid: v.id, i, o, left: Math.min(84, Math.max(0, x(o.p.ms) - 8)) });
                    };
                    return (
                      <div key={i} title={`${textoEntrada(m)} · ${textoSalida(m)}`} onMouseMove={enTramo}
                        onClick={(ev) => { ev.stopPropagation(); if (tip && tip.vid === v.id && tip.i === i) setTip(null); else enTramo(ev); }}
                        style={{ position: 'absolute', left: `${x(a)}%`, width: `${Math.max(0.8, x(b) - x(a))}%`, top: 0, bottom: 0, borderRadius: 4, background: ti.color }} />
                    );
                  })}
                  {tip && tip.vid === v.id && (
                    <div style={{ position: 'absolute', ...(fila < 2 ? { top: 22 } : { bottom: 22 }), left: `${tip.left}%`, width: 168, zIndex: 5, background: '#fff', border: `1px solid ${T.line}`, borderRadius: 8, padding: 4, boxShadow: '0 6px 18px rgba(0,0,0,.18)', pointerEvents: 'none' }}>
                      <Photo photo={tip.o.p} crop={recorteVeh(tip.o)} boxes={[{ ...tip.o.d, color: ti.color }]} style={{ borderRadius: 5 }} />
                      <div style={{ fontSize: 11, color: T.text, marginTop: 3, textAlign: 'center' }}>{fmtHM(tip.o.p.ms)} h</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {hora != null && (
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: `calc(${nombreW}px + (100% - ${nombreW}px) * ${x(hora) / 100})`, width: 2, background: T.blue, pointerEvents: 'none' }} />
          )}
        </div>
        {onHora && <div style={{ fontSize: 11, color: T.muted, marginTop: 6, marginLeft: nombreW }}>Cada barra es un rato en obra: pasá el mouse o tocá la barra para ver la foto de ese momento. Tocá el recorte para ver todas sus fotos del día. La línea azul es la hora que estás mirando.</div>}
      </div>
    </div>
  );
}

// Foto del momento elegido con los vehículos presentes marcados
function Momento({ fotosDia, idx, setIdx, filas, narrow, onVer }) {
  const p = fotosDia[idx];
  if (!p) return null;
  const presentes = filas.filter((f) => f.movs.some((m) => m.t.obs.some((o) => o.p.id === p.id)));
  const ausentes = filas.filter((f) => !presentes.includes(f));
  const cajas = presentes.flatMap((f) => f.movs.flatMap((m) => m.t.obs.filter((o) => o.p.id === p.id).map((o) => ({ ...o.d, color: typeInfo(f.v.type).color }))));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'minmax(0, 3fr) minmax(0, 2fr)', gap: 14 }}>
      <div style={{ minWidth: 0 }}>
        <Photo photo={p} boxes={cajas} onClick={() => onVer(p, cajas)} prioridad="alta" style={{ cursor: 'zoom-in', borderRadius: 10 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <Btn onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0}>‹</Btn>
          <input type="range" min={0} max={fotosDia.length - 1} value={idx} onChange={(e) => setIdx(Number(e.target.value))} style={{ flex: 1 }} aria-label="Hora del día" />
          <Btn onClick={() => setIdx(Math.min(fotosDia.length - 1, idx + 1))} disabled={idx === fotosDia.length - 1}>›</Btn>
          <b style={{ fontSize: 15, minWidth: 46, textAlign: 'right' }}>{fmtHM(p.ms)}</b>
        </div>
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#047857', marginBottom: 6 }}>En obra a las {fmtHM(p.ms)} ({presentes.length})</div>
        {presentes.length ? presentes.map((f) => (
          <div key={f.v.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '3px 0' }}>
            <Muestra color={f.v.color} />{vehicleName(f.v)}
          </div>
        )) : <div style={{ fontSize: 13, color: T.muted }}>Ningún vehículo en la foto.</div>}
        {!!ausentes.length && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.muted, margin: '12px 0 6px' }}>No están ahora (vinieron ese día) ({ausentes.length})</div>
            {ausentes.map((f) => {
              const antes = f.movs.filter((m) => m.sale < p.ms).pop();
              const despues = f.movs.find((m) => m.entra > p.ms);
              return (
                <div key={f.v.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '3px 0', color: T.muted }}>
                  <Muestra color={f.v.color} /><span style={{ color: T.text }}>{vehicleName(f.v)}</span>
                  <span style={{ fontSize: 11.5 }}>{despues ? `· llega ${fmtHM(despues.entra)}` : antes ? `· se fue ${fmtHM(antes.sale)}` : ''}</span>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

// Ficha del vehículo: todas sus entradas y salidas del período
function Ficha({ v, fotosPorDia, onCerrar, onVer, onVerFotos, narrow }) {
  const ti = typeInfo(v.type);
  return (
    <Card title={<><Muestra color={v.color} />{vehicleName(v)}<span style={{ fontWeight: 400, color: T.muted, fontSize: 12.5 }}>· vehículo probable</span></>} right={<Btn onClick={onCerrar}>Cerrar</Btn>}>
      <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '220px minmax(0, 1fr)', gap: 14 }}>
        <div>
          <Photo photo={v.best.p} crop={recorte(v.best.p, v.best.d, { k: 1.8, ratio: R169, min: 0.06 })} onClick={() => onVer(v.best.p, [{ ...v.best.d, color: ti.color }])} style={{ cursor: 'zoom-in', borderRadius: 10 }} />
          <div style={{ fontSize: 12, color: T.muted, marginTop: 6, lineHeight: 1.5 }}>
            {cap(ti.label)}{colorInfo(v.color) ? ` · ${colorInfo(v.color).label}` : ' · color sin determinar'}<br />
            En obra {plural(v.nDays, 'día', 'días')} del período · {plural(v.days.reduce((a, d) => a + d.tracks.length, 0), 'entrada', 'entradas')}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
          {v.days.map((dv) => {
            const fd = fotosPorDia.get(dv.day) || [];
            return (
              <div key={dv.day}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{cap(fmtDow(dv.day))} {fmtKey(dv.day)}</div>
                {movimientos(dv, fd).map((m, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', borderTop: i ? `1px dashed ${T.line}` : 'none' }}>
                    {[['↘', m.t.obs[0], '#047857'], ['↗', m.t.obs[m.t.obs.length - 1], '#b91c1c']].map(([fl, o, c], j) => (
                      <div key={j} style={{ width: narrow ? 84 : 104, flex: 'none' }}>
                        <Photo photo={o.p} crop={recorte(o.p, o.d, { k: 2, ratio: R169, min: 0.06 })} onClick={() => onVerFotos(fotosDeVehiculo(m.t.obs, ti.color))} style={{ cursor: 'zoom-in', borderRadius: 6 }} />
                        <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}><b style={{ color: c }}>{fl}</b> {fmtHM(o.p.ms)}</div>
                      </div>
                    ))}
                    <div style={{ fontSize: 12.5, lineHeight: 1.45 }}>
                      <div><b style={{ color: '#047857' }}>↘</b> {textoEntrada(m)}</div>
                      <div><b style={{ color: '#b91c1c' }}>↗</b> {textoSalida(m)}</div>
                      <div style={{ color: T.muted }}>≈ {dur(m.min)} en obra · {plural(m.t.obs.length, 'foto', 'fotos')}</div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

async function generarPdf({ name, rango, dias, vehiculos, fotosPorDia }) {
  const pdf = nuevoPdf({
    titulo: 'Entradas y salidas de vehículos', obra: name, periodo: textoRango(rango),
    nota: 'Vehículo probable: se reconoce al mismo vehículo por tipo, color y lugar donde estaciona (no se leen patentes). Con una foto cada 20 minutos aprox., cada entrada y salida tiene ese margen. Generado automáticamente a partir de las fotos de la cámara.',
  });
  for (const dia of dias) {
    const vs = vehiculos.filter((v) => v.days.some((d) => d.day === dia));
    if (!vs.length) continue;
    const fd = fotosPorDia.get(dia) || [];
    pdf.titulo(`${cap(fmtDow(dia))} ${fmtKey(dia)} · ${plural(vs.length, 'vehículo', 'vehículos')}`);
    const filas = [];
    for (const v of vs) {
      const dv = v.days.find((d) => d.day === dia);
      for (const m of movimientos(dv, fd)) {
        filas.push([m.entra, vehicleName(v), m.entraDesde == null ? `ya estaba ${fmtHM(m.entra)}` : `${fmtHM(m.entraDesde)}-${fmtHM(m.entra)}`, m.saleHasta == null ? `seguía ${fmtHM(m.sale)}` : `${fmtHM(m.sale)}-${fmtHM(m.saleHasta)}`, dur(m.min)]);
      }
    }
    pdf.tabla([{ t: 'Vehículo (probable)', w: 70 }, { t: 'Entrada', w: 38 }, { t: 'Salida', w: 38 }, { t: 'En obra', w: 36 }], filas.sort((a, b) => a[0] - b[0]).map((f) => f.slice(1)));
    // Fotos de los vehículos del día (de a 4)
    for (let i = 0; i < vs.length; i += 4) {
      const grupo = vs.slice(i, i + 4);
      // eslint-disable-next-line no-await-in-loop
      const imgs = await Promise.all(grupo.map((v) => {
        const t = v.days.find((d) => d.day === dia).tracks[0];
        return imagenRecorte(t.best.p, recorte(t.best.p, t.best.d, { k: 1.8, ratio: R169, min: 0.06 }), [{ ...t.best.d, color: typeInfo(v.type).color }], 420);
      }));
      // eslint-disable-next-line no-await-in-loop
      await pdf.imagenes(grupo.map((v, j) => ({ img: imgs[j], leyenda: vehicleName(v) })), { alto: 26 });
    }
  }
  pdf.guardar(nombreArchivo('entradas-salidas', name, rango));
}

export default function EntradasSalidas({ feedId, name }) {
  const narrow = useNarrow();
  const { rango, setRango, cargando, fotos, fuente } = useFotosV3(feedId, 7);
  const [colores, setColores] = useState(new Map());
  const [dia, setDia] = useState(null);
  const [idx, setIdx] = useState(0);
  const [sel, setSel] = useState(null);
  const [visor, setVisor] = useState(null);
  const [haciendoPdf, setHaciendoPdf] = useState(false);
  const [tipo, setTipo] = useState('todos');

  const tracks = useMemo(() => buildTracks(fotos), [fotos]);
  useEffect(() => {
    let vivo = true;
    setColores(new Map());
    if (tracks.length) computeColors(tracks, (c) => vivo && setColores(c), () => vivo);
    return () => { vivo = false; };
  }, [tracks]);
  const vehiculos = useMemo(() => buildVehicles(tracks, colores).filter((v) => !v.fixed), [tracks, colores]);
  const fotosPorDia = useMemo(() => {
    const m = porDia(fotos.filter((p) => { const h = new Date(p.ms - 3 * 3600e3).getUTCHours(); return h >= H0 && h < H1; }));
    return m;
  }, [fotos]);
  const diasConDatos = useMemo(() => [...fotosPorDia.keys()].sort(), [fotosPorDia]);
  useEffect(() => {
    if (!diasConDatos.length) return;
    // Por defecto el último día con la jornada completa (8 fotos o más); si no hay, el último
    if (!dia || !diasConDatos.includes(dia)) setDia([...diasConDatos].reverse().find((k) => fotosPorDia.get(k).length >= 8) || diasConDatos[diasConDatos.length - 1]);
  }, [diasConDatos, dia, fotosPorDia]);
  const fotosDia = useMemo(() => (dia && fotosPorDia.get(dia)) || [], [dia, fotosPorDia]);
  useEffect(() => {
    // Por defecto, la foto con más vehículos del día
    if (!fotosDia.length) return;
    setIdx(fotoPrincipal(fotosDia, tracks));
  }, [dia]); // eslint-disable-line react-hooks/exhaustive-deps
  // Precarga con prioridad baja: las fotos vecinas de la barra (para moverla sin esperar) y, del día anterior y del
  // siguiente, la foto principal (1280 px) y los recortes de la línea de tiempo. Cambiar de día queda instantáneo.
  useEffect(() => {
    if (!fotosDia.length) return;
    precargar([fotosDia[idx + 1], fotosDia[idx - 1]].filter(Boolean).map((p) => [p, 1280]));
    precargar(fotosDia.slice(Math.max(0, idx - 4), idx + 5).map((p) => [p, 320]));
  }, [idx, fotosDia]);
  useEffect(() => {
    if (!dia) return;
    const i = diasConDatos.indexOf(dia);
    const fotoW = narrow ? 96 : 112;
    const lista = [];
    for (const k of [diasConDatos[i + 1], diasConDatos[i - 1]]) {
      const fd = (k && fotosPorDia.get(k)) || [];
      if (!fd.length) continue;
      lista.push([fd[fotoPrincipal(fd, tracks)], 1280]);
      for (const v of vehiculos) {
        const dv = v.days.find((d) => d.day === k);
        if (!dv) continue;
        const o = mejorObs(dv.tracks.flatMap((t) => t.obs));
        lista.push([o.p, anchoObjetivo(Math.ceil(fotoW / 40) * 40, o.p, recorteVeh(o))]);
      }
    }
    precargar(lista);
  }, [dia, diasConDatos, fotosPorDia, vehiculos, tracks, narrow]);

  const tipos = useMemo(() => [...new Set(vehiculos.map((v) => v.type))], [vehiculos]);
  const filas = useMemo(() => vehiculos
    .filter((v) => tipo === 'todos' || v.type === tipo)
    .map((v) => { const dv = v.days.find((d) => d.day === dia); return dv ? { v, dv, movs: movimientos(dv, fotosDia) } : null; })
    .filter(Boolean)
    .sort((a, b) => a.dv.first - b.dv.first), [vehiculos, dia, fotosDia, tipo]);

  const kpis = useMemo(() => {
    const entradas = filas.reduce((a, f) => a + f.movs.filter((m) => m.entraDesde != null).length, 0);
    let maxSim = 0, horaMax = null;
    fotosDia.forEach((p) => { const k = filas.filter((f) => f.movs.some((m) => m.t.obs.some((o) => o.p.id === p.id))).length; if (k > maxSim) { maxSim = k; horaMax = p.ms; } });
    const pesados = filas.filter((f) => ['camion', 'volquete', 'mixer', 'bomba'].includes(f.v.type)).length;
    return { vehiculos: filas.length, entradas, maxSim, horaMax, pesados };
  }, [filas, fotosDia]);

  const vSel = vehiculos.find((v) => v.id === sel);
  const ver = (p, cajas) => setVisor([{ photo: p, label: `${fmtDM(p.ms)} ${fmtHM(p.ms)}`, boxes: cajas, zoom: cajas[0] ? recorte(p, cajas[0], { k: 3, ratio: R169, min: 0.15 }) : null }]);
  const pdf = async (soloDia) => {
    setHaciendoPdf(true);
    try { await generarPdf({ name, rango: soloDia ? { desde: dia, hasta: dia } : rango, dias: soloDia ? [dia] : diasConDatos, vehiculos: vehiculos.filter((v) => tipo === 'todos' || v.type === tipo), fotosPorDia }); } finally { setHaciendoPdf(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Encabezado icono="🚦" titulo="Entradas y salidas" obra={name} bajada="Qué vehículos están en la obra, cuándo entraron y cuándo se fueron">
        <Rango rango={rango} setRango={setRango} />
      </Encabezado>
      {cargando ? <Card><Cargando /></Card> : fuente !== 'iaV3' ? <Card><SinAnalisis /></Card> : !diasConDatos.length ? <Card><Vacio>No hay fotos de día en el {textoRango(rango)}.</Vacio></Card> : !dia ? <Card><Cargando /></Card> : (
        <>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {diasConDatos.map((k) => <Btn key={k} active={k === dia} onClick={() => { setDia(k); setSel(null); }}>{cap(fmtDow(k))} {k.slice(8)}/{k.slice(5, 7)}</Btn>)}
            <span style={{ flex: 1 }} />
            <Btn onClick={() => pdf(true)} disabled={haciendoPdf}>{haciendoPdf ? 'Armando PDF…' : '⬇ PDF del día'}</Btn>
            <Btn onClick={() => pdf(false)} disabled={haciendoPdf}>⬇ PDF del período</Btn>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${narrow ? 2 : 4}, minmax(0, 1fr))`, gap: 10 }}>
            <Kpi label="Vehículos distintos" valor={kpis.vehiculos} sub="probables, en el día" />
            <Kpi label="Entradas" valor={kpis.entradas} sub="sin contar los que ya estaban" />
            <Kpi label="Camiones, mixers y bombas" valor={kpis.pesados} />
            <Kpi label="Máximo al mismo tiempo" valor={kpis.maxSim} sub={kpis.horaMax ? `a las ${fmtHM(kpis.horaMax)}` : null} />
          </div>
          <Card title={<span>Quién está en la obra</span>} right={<span style={{ fontSize: 12, color: T.muted }}>Mové la barra para ver otra hora</span>}>
            <Momento fotosDia={fotosDia} idx={idx} setIdx={setIdx} filas={filas} narrow={narrow} onVer={ver} />
          </Card>
          <Card title={<span>Línea de tiempo del {fmtKey(dia)}</span>} right={(
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Chip on={tipo === 'todos'} onClick={() => setTipo('todos')}>Todos</Chip>
              {tipos.map((t) => <Chip key={t} color={typeInfo(t).color} on={tipo === t} onClick={() => setTipo(tipo === t ? 'todos' : t)}>{typeInfo(t).label}</Chip>)}
            </div>
          )}>
            {filas.length ? <Gantt filas={filas} dia={dia} hora={fotosDia[idx]?.ms} onHora onElegir={(id) => setSel(sel === id ? null : id)} sel={sel} narrow={narrow} onVerFotos={setVisor} />
              : <Vacio>No se vieron vehículos ese día.</Vacio>}
            <div style={{ fontSize: 12, color: T.muted, marginTop: 8 }}>Tocá un vehículo para ver todas sus entradas y salidas.</div>
          </Card>
          {vSel && <Ficha v={vSel} fotosPorDia={fotosPorDia} narrow={narrow} onCerrar={() => setSel(null)} onVer={ver} onVerFotos={setVisor} />}
          <Nota tono="aviso">
            <b>Vehículo probable.</b> Reconocemos al mismo vehículo entre fotos por su tipo, su color y el lugar donde estaciona; las patentes no se
            llegan a leer desde la cámara. Dos vehículos iguales (por ejemplo, dos camionetas blancas) pueden contarse como uno, y uno que se estacionó
            en otro lugar puede contarse como dos. La cámara saca una foto cada 20 minutos aprox.: por eso cada entrada y salida se muestra como
            un rango (“entró entre 11:40 y 12:00”), y una visita más corta que eso puede no aparecer.
          </Nota>
        </>
      )}
      {visor && <Visor fotos={visor} inicio={0} zoomZona={visor[0].zoom} onClose={() => setVisor(null)} />}
    </div>
  );
}
